import React from 'react';
import { Box, Typography, Paper, Chip, Stack, Avatar } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import DI from './utility/DI';
import MCPOAuthSection from './utility/MCPOAuthSection';
import versionInfo from '../version.json';
import {
  Home, Users, MapPin, LayoutDashboard, Clock, Server,
  GitBranch, GitCommit, Calendar, Layers, Sparkles,
  Shield, PieChart, Upload, Bot, UserCheck, BarChart3,
  Wallet, Zap, ArrowRight
} from 'lucide-react';

const About = (props) => {
  const { di: { session: userData } } = props;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const p = {
    purple: '#7c3aed', blue: '#2563eb', emerald: '#059669',
    amber: '#d97706', rose: '#e11d48', indigo: '#4f46e5',
    cyan: '#0891b2', teal: '#0d9488', slate: '#475569',
  };

  const cardSx = {
    p: 2, mb: 1.5, borderRadius: 3, border: 'none',
    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.015),
    boxShadow: isDark
      ? `0 1px 3px ${alpha('#000', 0.4)}, inset 0 1px 0 ${alpha('#fff', 0.04)}`
      : `0 1px 3px ${alpha('#000', 0.06)}, 0 1px 2px ${alpha('#000', 0.04)}`,
  };

  const updates = [
    { text: 'Modal dialogs replaced native alerts', tag: 'UX', color: p.blue, icon: <Shield size={12} /> },
    { text: 'E2E test suite with Playwright', tag: 'QA', color: p.amber, icon: <Zap size={12} /> },
    { text: 'Home wallet with split summaries', tag: 'NEW', color: p.emerald, icon: <PieChart size={12} /> },
    { text: 'Bill image capture & upload', tag: 'NEW', color: p.emerald, icon: <Upload size={12} /> },
    { text: 'AI-ready — Claude & ChatGPT via MCP', tag: 'AI', color: p.purple, icon: <Bot size={12} /> },
    { text: 'OAuth with granular MCP permissions', tag: 'AI', color: p.purple, icon: <Shield size={12} /> },
    { text: 'Identity endpoint (/whoami)', tag: 'API', color: p.cyan, icon: <UserCheck size={12} /> },
    { text: 'Interactive bar & pie charts', tag: 'DATA', color: p.rose, icon: <BarChart3 size={12} /> },
    { text: 'Expense approval workflow', tag: 'FLOW', color: p.teal, icon: <Clock size={12} /> },
  ];

  const modules = [
    { icon: <MapPin size={20} />, name: 'Shared', sub: 'Split & settle', color: p.purple },
    { icon: <Home size={20} />, name: 'Home', sub: 'Multi-house', color: p.blue },
    { icon: <Users size={20} />, name: 'Friends', sub: 'Connections', color: p.amber },
    { icon: <LayoutDashboard size={20} />, name: 'Dashboard', sub: 'Analytics', color: p.emerald },
    { icon: <Clock size={20} />, name: 'Pendings', sub: 'Approvals', color: p.rose },
    { icon: <Server size={20} />, name: 'MCP', sub: 'AI connect', color: p.indigo },
  ];

  // Icon badge — colored circle behind icon
  const IconBadge = ({ color, children, size = 28 }) => (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      bgcolor: alpha(color, isDark ? 0.15 : 0.1),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: color,
    }}>{children}</Box>
  );

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 1.5, py: 2, pb: 12 }}>

      {/* ── PROFILE HERO ── */}
      {userData && (
        <Box sx={{
          p: 2.5, mb: 1.5, borderRadius: 3, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, ${p.indigo} 0%, ${p.purple} 50%, ${p.rose} 100%)`,
          boxShadow: `0 8px 24px ${alpha(p.purple, 0.35)}`,
        }}>
          {/* Decorative shapes */}
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: 40, width: 70, height: 70, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />
          <Box sx={{ position: 'absolute', top: 10, right: 60, width: 30, height: 30, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />

          <Stack direction="row" alignItems="center" gap={2} sx={{ position: 'relative', zIndex: 1 }}>
            <Avatar sx={{
              width: 48, height: 48, fontSize: 17, fontWeight: 800, letterSpacing: 1,
              bgcolor: 'rgba(255,255,255,0.18)', color: '#fff',
              border: '2px solid rgba(255,255,255,0.25)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            }}>
              {userData.firstname?.[0]?.toUpperCase()}{userData.lastname?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#fff', textTransform: 'capitalize', letterSpacing: 0.3 }} noWrap>
                {userData.firstname} {userData.lastname}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', mt: 0.25 }} noWrap>{userData.email}</Typography>
            </Box>
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
              backdropFilter: 'blur(8px)',
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>#{userData.id}</Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {/* ── BUILD ── */}
      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
          <Stack direction="row" alignItems="center" gap={1}>
            <IconBadge color={p.indigo}><Layers size={14} /></IconBadge>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary', letterSpacing: -0.2 }}>Build Info</Typography>
          </Stack>
          <Box sx={{
            px: 1.25, py: 0.35, borderRadius: 2,
            bgcolor: alpha(p.emerald, isDark ? 0.15 : 0.08),
            border: `1px solid ${alpha(p.emerald, 0.25)}`,
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: p.emerald, fontFamily: 'monospace' }}>v{versionInfo.version}</Typography>
          </Box>
        </Stack>

        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {[
            { icon: <Calendar size={10} />, label: versionInfo.buildDate, color: p.slate },
            { icon: <GitBranch size={10} />, label: versionInfo.gitBranch, color: p.emerald },
            { icon: <GitCommit size={10} />, label: versionInfo.gitCommit, color: p.indigo },
          ].map((c, i) => (
            <Box key={i} sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.4, borderRadius: 1.5,
              bgcolor: alpha(c.color, isDark ? 0.1 : 0.06),
              border: `1px solid ${alpha(c.color, isDark ? 0.2 : 0.12)}`,
            }}>
              <Box sx={{ color: c.color, display: 'flex' }}>{c.icon}</Box>
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: c.color, fontFamily: 'monospace' }}>{c.label}</Typography>
            </Box>
          ))}
        </Stack>

        {versionInfo.gitCommitMessage && versionInfo.gitCommitMessage !== 'Local build' && (
          <Typography sx={{ mt: 1, fontSize: 10, color: 'text.disabled', fontStyle: 'italic' }}>
            ↳ "{versionInfo.gitCommitMessage}"
          </Typography>
        )}
      </Box>

      {/* ── CHANGELOG ── */}
      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <IconBadge color={p.amber}><Sparkles size={14} /></IconBadge>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary', letterSpacing: -0.2 }}>What's New</Typography>
          </Stack>
          <Typography sx={{ fontSize: 10, color: 'text.disabled', fontFamily: 'monospace' }}>v{versionInfo.version}</Typography>
        </Stack>

        <Stack gap={0.5}>
          {updates.map((u, i) => (
            <Stack key={i} direction="row" alignItems="center" gap={1} sx={{
              py: 0.6, px: 1, mx: -1, borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(u.color, isDark ? 0.1 : 0.05),
                transform: 'translateX(2px)',
              }
            }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: 1.5, flexShrink: 0,
                bgcolor: alpha(u.color, isDark ? 0.12 : 0.08),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: u.color,
              }}>{u.icon}</Box>
              <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>{u.text}</Typography>
              <Typography sx={{
                fontSize: 9, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
                color: u.color, flexShrink: 0, opacity: 0.7,
              }}>{u.tag}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* ── MCP ── */}
      {userData && <MCPOAuthSection userId={userData.id} />}

      {/* ── MODULES ── */}
      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <IconBadge color={p.indigo}><Layers size={14} /></IconBadge>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary', letterSpacing: -0.2 }}>Platform</Typography>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {modules.map((m, i) => (
            <Box key={i} sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
              py: 1.5, px: 1, borderRadius: 2.5,
              bgcolor: alpha(m.color, isDark ? 0.08 : 0.04),
              border: `1.5px solid ${alpha(m.color, isDark ? 0.2 : 0.1)}`,
              transition: 'all 0.25s ease',
              cursor: 'default',
              '&:hover': {
                bgcolor: alpha(m.color, isDark ? 0.15 : 0.08),
                borderColor: alpha(m.color, 0.35),
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${alpha(m.color, 0.2)}`,
              }
            }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                bgcolor: alpha(m.color, isDark ? 0.15 : 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: m.color,
              }}>{m.icon}</Box>
              <Box textAlign="center">
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>{m.name}</Typography>
                <Typography sx={{ fontSize: 9, color: 'text.secondary', mt: 0.25 }}>{m.sub}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── ABOUT ── */}
      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <IconBadge color={p.blue}><Wallet size={14} /></IconBadge>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary', letterSpacing: -0.2 }}>About</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.7, fontWeight: 400 }}>
          Full-stack personal finance platform — track expenses across homes,
          split bills with friends, visualize spending with charts, and connect
          your data to AI assistants via the Model Context Protocol.
        </Typography>
      </Box>

      {/* ── FOOTER ── */}
      <Stack direction="row" justifyContent="center" alignItems="center" gap={0.75} mt={2} mb={1}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: p.emerald, boxShadow: `0 0 6px ${p.emerald}` }} />
        <Typography sx={{ fontSize: 9, color: 'text.disabled', letterSpacing: 1.2, fontWeight: 600, textTransform: 'uppercase' }}>
          Spent · v{versionInfo.version} · {new Date().getFullYear()}
        </Typography>
      </Stack>
    </Box>
  );
};

export default DI(About);
